import { HttpClient, HttpEventType, HttpRequest } from '@angular/common/http';
import { Component, Inject, Input, OnInit, ViewChild } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { Mutation, Query, ResourceUploadSigningRequest } from 'core-lib';
import { MessageService, ToastMessageOptions } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FileUpload } from 'primeng/fileupload';
import { Message } from 'primeng/message';
import { forkJoin, Observable } from 'rxjs';

@Component({
    selector: 'lib-media-upload',
    templateUrl: './media-upload.component.html',
    styleUrls: ['./media-upload.component.scss'],
    standalone: false
})
export class MediaUploadComponent implements OnInit {
  @ViewChild('fileUpload') fileUploadControl: FileUpload;
  @Input() accept: string = "image/*,.pdf,video/*,audio/mp3";
  files: File[] = [];
  uploadStatus = {
    running: false, progress: {}
  }

  SIGN_FILE_UPLOAD_MUTATION = gql`
    mutation CreateSignedResourceUpload($requests:[ResourceUploadSigningRequest]!){
      createSignedResourceUpload(requests: $requests){
        filename
        resource{
          id
        }
        uploadUrl
        error
      }
    }`;

  COMPLETED_FILE_UPLOAD_MUTATION = gql`
    mutation CreateSignedResourceUploadCompleted($resourceIds:[String]!){
      createSignedResourceUploadCompleted(resourceIds: $resourceIds){
        id
      }
    }`;


  RESOURCE_FINDBYFILENAME_QUERY = gql`
    query findResourceByFilename($filename: String!, $mimeType: String!){
      findResourceByFilename(filename: $filename, mimeType: $mimeType){
        id
      }
    }`;
  constructor(private messageService: MessageService,
    @Inject('jwt') public jwt,
    @Inject('environment') private environment: any,
    private dialogConfig: DynamicDialogConfig,
    private dialogRef: DynamicDialogRef,
    private http: HttpClient,
    private apollo: Apollo) {
    if (this.dialogConfig && this.dialogConfig.data && this.dialogConfig.data.accept) {
      if (this.dialogConfig.data.accept.indexOf('image') > -1) {
        this.accept = "image/jpg,image/jpeg,image/png,image/svg+xml,image/gif,image/webp";
      } else {
        this.accept = this.dialogConfig.data.accept;
      }
    }
    this.accept = "image/jpg,image/jpeg,image/png,image/svg+xml,image/gif,image/webp,.pdf,video/*,.mp3";
  }

  ngOnInit(): void {
  }

  onRemove(event): void {
    const index = this.files.indexOf(event.file);
    if (index > -1) {
      this.files.splice(index, 1);
    }
  }
  
  onSelect(event): void {
    this.files = event.currentFiles;
    this.files.forEach((file) => {
      this.apollo.query<Query>({
        query: this.RESOURCE_FINDBYFILENAME_QUERY,
        variables: { mimeType: file.type, filename: file.name }
      }).subscribe((result) => {
        file["exist"] = result.data.findResourceByFilename != null;
      });
    })
  }

  doUpload(event): void {
    const files = event.files as File[];
    const requests = files.map((file) => {
      const request = {} as ResourceUploadSigningRequest;
      request.contentLength = file.size;
      request.filename = file.name
      request.mimeType = file.type;
      return request;
    });
    this.uploadStatus.running = true;
    this.apollo.query<Mutation>({
      query: this.SIGN_FILE_UPLOAD_MUTATION,
      variables: { requests: requests }
    }).subscribe((result) => {
      if (!result.errors) {
        const uploadSigningResponses = result.data.createSignedResourceUpload;
        this.uploadStatus.progress = {};
        const observableUploadRequets = uploadSigningResponses.map((uploadSigningResponse, index) => {
          return new Observable<any>((ob) => {
            const putUrl = uploadSigningResponse.uploadUrl;
            const file = files[index];
            const resource = uploadSigningResponse.resource;
            const req = new HttpRequest('PUT', putUrl, file, {
              reportProgress: true
            });
            this.uploadStatus.progress[file.name] = { completed: 0, status: 0, message: null };
            if (uploadSigningResponse.error) {
              ob.next({ event: { status: 500, statusText: uploadSigningResponse.error }, file: file });
              ob.complete();
              return;
            }
            this.http.request(req).subscribe((event) => {
              ob.next({ file: files[index], event: event, resource: resource });
              if (event.type === HttpEventType.Response) {
                ob.complete();
              } else if (event.type === HttpEventType.UploadProgress) {
                const percentDone = Math.round(100 * event.loaded / (event.total ?? 0));
                this.uploadStatus.progress[file.name].completed = percentDone;
              }
            });
          });
        });
        forkJoin(observableUploadRequets).subscribe((uploadResult) => {
          const resourceIds: string[] = [];
          let failedUploadCounter = 0;
          for (const result of uploadResult) {
            this.uploadStatus.progress[result.file.name].status = result.event.status;
            if (result.event.status != 200) {
              this.uploadStatus.progress[result.file.name].message = result.event.statusText;
              failedUploadCounter++;
            } else {
              resourceIds.push(result.resource.id);
              const index = this.files.indexOf(result.file);
              if (index > -1) {
                this.files.splice(index, 1);
              }
            }
          }
          this.apollo.query<Mutation>({
            query: this.COMPLETED_FILE_UPLOAD_MUTATION,
            variables: { resourceIds: resourceIds }
          }).subscribe((result) => {
            if (!result.errors) {
              if (failedUploadCounter == 0) {
                this.messageService.add({ severity: 'success', summary: $localize`:@@media-upload.message.success:Upload successful` } as ToastMessageOptions);
                setTimeout(() => {
                  this.dialogRef.close({ success: true })
                }, 1000);
              } else {
                this.messageService.add({ severity: 'error', summary: $localize`:@@media-upload.message.error:Upload not successful` } as ToastMessageOptions);
              }
            }
          });
          this.uploadStatus.running = false;
        });
      } else {
        this.uploadStatus.running = false;
      }
    });
  }

  /**
   * helpers
   * @param bytes 
   * @returns 
   */
  formatSize(bytes) {
    if (bytes == 0) {
      return '0 B';
    }
    let k = 1000,
      dm = 3,
      sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'],
      i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  isImage(file: File): boolean {
    return /^image\//.test(file.type);
  }

  remove(file: File) {
    this.files.splice(this.files.indexOf(file), 1);
    delete this.uploadStatus.progress[file.name];
  }

  removeAll() {
    this.files.splice(0,this.files.length);
    this.uploadStatus.progress = {};
  }
}
